/**
 * Inline Markdown/formatting used in exercise body text (shared by interactive and open pages).
 */
import { parseImageAltWidth, getImageEmbedStyle } from './imageEmbedSizes.js';

const IMG_PH_PREFIX = '{{MDIMG:';
const IMG_PH_SUFFIX = '}}';
const LNK_PH_PREFIX = '{{MDLNK:';
const LNK_PH_SUFFIX = '}}';
const BLANK_PH_PREFIX = '{{MDBLANK:';
const BLANK_PH_SUFFIX = '}}';
const HTML_MEDIA_PH_PREFIX = '{{MDHTML:';
const HTML_MEDIA_PH_SUFFIX = '}}';

const TTS_BTN_STYLE =
    'display:inline-flex;align-items:center;justify-content:center;background:#eef2ff;border:1px solid #c7d2fe;border-radius:8px;cursor:pointer;font-size:1rem;line-height:1;padding:4px 8px;margin:0 3px 0 0;vertical-align:middle;color:#4338ca;user-select:none';

/**
 * Icon-only TTS control. Spoken text lives in `data-tts` only — playback is handled by
 * document click delegation in `registerGlobalSpeak` (avoids broken onclick when the
 * phrase contains apostrophes, and works inside contenteditable / DOMParser round-trips).
 */
export function buildExerciseTtsButtonHtml(spoken) {
    const text = String(spoken ?? '');
    const title = text
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return (
        `<button type="button" class="ex-tts-btn" data-ex-marker="tts" data-tts="${title}" ` +
        `contenteditable="false" ` +
        `title="${title}" aria-label="Play audio" style="${TTS_BTN_STYLE}">🔊</button>`
    );
}

/** Turn leaked Visual-editor TTS chips back into `[$…$]` so formatInline can render buttons. */
function normalizeLeakedTtsChips(text) {
    return String(text || '')
        .replace(
            /<(?:span|button)\b([^>]*\bdata-ex-marker=["']tts["'][^>]*)>[\s\S]*?<\/(?:span|button)>/gi,
            (_, attrs) => {
                // Prefer double-quoted data-tts so ASCII apostrophes in You're etc. survive.
                const m = String(attrs).match(/\bdata-tts=(?:"([^"]*)"|'([^']*)')/i);
                if (!m) return '[$ $]';
                const spoken = (m[1] ?? m[2] ?? '')
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;/g, "'")
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&amp;/g, '&');
                return `[$${spoken}$]`;
            }
        );
}

/** Underscore runs of 3+ are visual blanks (_____), not __bold__ / _italic_. */
function shieldUnderscoreBlanks(text) {
    const blanks = [];
    const safe = text.replace(/_{3,}/g, (match) => {
        const idx = blanks.length;
        blanks.push(match);
        return `${BLANK_PH_PREFIX}${idx}${BLANK_PH_SUFFIX}`;
    });
    return { safe, blanks };
}

function restoreUnderscoreBlanks(text, blanks) {
    return text.replace(/\{\{MDBLANK:(\d+)\}\}/g, (_, idx) => blanks[Number(idx)] || '');
}

function shieldHtmlMediaTags(text) {
    const tags = [];
    const push = (match) => {
        const i = tags.length;
        tags.push(match);
        return `${HTML_MEDIA_PH_PREFIX}${i}${HTML_MEDIA_PH_SUFFIX}`;
    };
    let safe = String(text || '');
    safe = safe.replace(/<(audio|iframe|video|source)\b[^>]*>[\s\S]*?<\/\1>/gi, push);
    safe = safe.replace(/<(audio|iframe|video|source|img)\b[^>]*\/?>/gi, push);
    return { safe, tags };
}

function restoreHtmlMediaTags(text, tags) {
    return String(text || '').replace(/\{\{MDHTML:(\d+)\}\}/g, (_, idx) => tags[Number(idx)] || '');
}

function shieldMarkdownMedia(text) {
    const images = [];
    const links = [];

    let safe = text.replace(/!\[(.*?)\]\((.*?)\)/g, (match, alt, url) => {
        const idx = images.length;
        images.push({ alt, url });
        return `${IMG_PH_PREFIX}${idx}${IMG_PH_SUFFIX}`;
    });

    safe = safe.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (match, label, url) => {
        const idx = links.length;
        links.push({ label, url });
        return `${LNK_PH_PREFIX}${idx}${LNK_PH_SUFFIX}`;
    });

    return { safe, images, links };
}

function restoreMarkdownMedia(text, images, links, { inlineImages = false } = {}) {
    let out = text.replace(/\{\{MDLNK:(\d+)\}\}/g, (_, idx) => {
        const link = links[Number(idx)];
        if (!link) return '';
        const labelHtml = formatInlineEmphasisOnly(link.label);
        return `<a href="${link.url}" target="_blank" rel="noopener noreferrer">${labelHtml}</a>`;
    });

    out = out.replace(/\{\{MDIMG:(\d+)\}\}/g, (_, idx) => {
        const img = images[Number(idx)];
        if (!img) return '';
        const widthPercent = parseImageAltWidth(img.alt);
        const style = getImageEmbedStyle(widthPercent, { inline: inlineImages });
        return `<img src="${img.url}" class="embedded-image" alt="${img.alt}" loading="lazy" decoding="async" style="${style}"/>`;
    });

    return out;
}

function applyNestedColorBgSize(html, { color, bg, size }) {
    let s = String(html || '');
    let prev;
    do {
        prev = s;
        s = s.replace(/\{c:([^}]+)\}([^{]*?)\{\/c\}/g, color);
        s = s.replace(/\{bg:([^}]+)\}([^{]*?)\{\/bg\}/g, bg);
        s = s.replace(/\{s:([^}]+)\}([^{]*?)\{\/s\}/g, size);
    } while (s !== prev);
    return s;
}

/** Emphasis/color formatting only — used inside link labels after media URLs are shielded. */
function formatInlineEmphasisOnly(text) {
    if (!text) return '';
    const { safe, blanks } = shieldUnderscoreBlanks(text);
    let html = safe
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/__(.+?)__/g, '<strong>$1</strong>')
        .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
        .replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');
    html = applyNestedColorBgSize(html, {
        color: '<span style="color:$1">$2</span>',
        bg: '$2',
        size: '$2',
    });
    return restoreUnderscoreBlanks(html, blanks);
}

export function formatInline(text, state = {}, options = {}) {
    if (!text) return '';
    if (state.counter === undefined) state.counter = 0;

    const normalized = normalizeLeakedTtsChips(text);
    const { safe: htmlSafe, tags: htmlTags } = shieldHtmlMediaTags(normalized);
    const { safe: mediaSafe, images, links } = shieldMarkdownMedia(htmlSafe);
    const { safe, blanks } = shieldUnderscoreBlanks(mediaSafe);

    let html = safe
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/__(.+?)__/g, '<strong>$1</strong>')
        .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
        .replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');
    html = applyNestedColorBgSize(html, {
        color: '<span style="color:$1">$2</span>',
        bg: '<span style="background-color:$1;padding:0 4px;border-radius:4px;display:inline-block;">$2</span>',
        size: '<span style="font-size:$1">$2</span>',
    });
    html = html
        .replace(/\|\|(.*?)\|\|/g, (match, p1) => {
            const blurId = `${state.prefix || 'txt'}_${state.counter++}`;
            const isUnblurred = state.unblurredMap?.[blurId];
            return `<span class="blurred-text ${isUnblurred ? 'unblurred' : ''}" data-blur-id="${blurId}">${p1}</span>`;
        })
        .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>')
        .replace(/\[\$(.*?)\$\]/g, (_, spoken) => buildExerciseTtsButtonHtml(spoken));

    html = restoreUnderscoreBlanks(html, blanks);
    html = restoreMarkdownMedia(html, images, links, options);
    return restoreHtmlMediaTags(html, htmlTags);
}

export function formatInlineToHtml(text, prefix = 'open', options = {}) {
    return formatInline(text, { prefix, unblurredMap: {} }, options);
}
